<template>
  <div class="ls-reg-page">
    <!-- Logo area -->
    <div class="ls-logo-area">
      <svg width="260" height="204" viewBox="0 0 250 196" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M97.3286 24.6607L217.049 24.6538C216.439 26.5896 215.468 29.0454 214.759 30.9866L210.386 42.9378L165.568 42.9393C156.165 42.9403 146.512 43.0695 137.145 42.9213C137.807 45.6955 138.622 49.8656 139.098 52.6815L198.424 52.6796L206.743 52.6745C201.531 67.3778 195.578 82.0879 190.362 96.7995C177.531 97.9917 164.554 98.6221 151.709 99.7579C148.482 100.043 145.038 100.345 141.806 100.474C141.67 93.3225 141.793 85.6755 141.808 78.4938L159.016 77.1112C160.017 74.0217 161.007 70.1133 161.889 66.9324C147.729 67.8467 133.527 69.3149 119.363 70.2918C115.468 70.5605 111.448 70.8615 107.572 71.2591C106.718 67.9313 105.964 63.9811 105.22 60.5788L100.155 37.3992C99.255 33.283 98.3545 28.7224 97.3286 24.6607Z" fill="#771AFF"/>
        <path d="M56.2523 6.81316C65.3134 6.74681 74.3749 6.76146 83.4357 6.85712C83.8478 9.43366 84.8431 13.3612 85.4249 15.9836L89.3124 33.5178L100.019 81.7885C103.343 81.4581 106.934 81.2537 110.284 81.005L132.089 79.3712C132.006 86.6125 132.043 94.0206 132.077 101.266L113.861 102.685L74.387 105.763L56.5951 25.0364C51.3665 24.5678 46.3941 26.5953 41.5962 22.5136C36.7298 18.3737 38.808 8.8706 44.926 7.28144C47.3592 6.6494 53.3553 6.85299 56.2523 6.81316Z" fill="#771AFF"/>
        <path d="M95.3172 109.109C102.737 108.234 109.456 113.578 110.322 121.043C111.188 128.508 105.873 135.265 98.4523 136.133C91.0365 137 84.3252 131.656 83.4597 124.197C82.5944 116.737 87.9022 109.983 95.3172 109.109Z" fill="#771AFF"/>
        <path d="M157.284 109.108C164.691 108.24 171.4 113.569 172.277 121.02C173.153 128.472 167.869 135.23 160.464 136.127C153.04 137.024 146.3 131.691 145.421 124.219C144.541 116.749 149.856 109.979 157.284 109.108Z" fill="#771AFF"/>
      </svg>
      <p class="ls-brand-name">{{ $t('login.brandName') }}</p>
    </div>

    <!-- Registration card -->
    <div class="ls-card">
      <h2 class="ls-card-title">{{ $t('signup.createAccountTitle') }}</h2>

      <!-- Step indicator -->
      <div class="ls-steps">
        <span
          v-for="n in 3"
          :key="n"
          class="ls-step"
          :class="{ 'ls-step--active': signupStep >= n }"
        >{{ n }}</span>
      </div>

      <!-- Step 1: Phone number -->
      <div v-if="signupStep === 1">
        <p class="ls-step-hint">{{ $t('signup.step1Hint') }}</p>
        <div class="ls-field">
          <CountryPhoneInput
            v-model="signupForm.phone_number"
            :label="$t('columns.phoneNumber')"
            placeholder="7701234567"
            @update:isValid="signupPhoneValid = $event"
          />
          <p v-if="step1Error" class="ls-error-text">{{ step1Error }}</p>
        </div>
        <button
          class="ls-btn ls-btn--primary"
          :class="{ 'ls-btn--loading': signupLoading }"
          :disabled="signupLoading || !signupPhoneValid"
          @click="sendOtp()"
        >
          <span v-if="!signupLoading">{{ $t('signup.sendCodeButton') }}</span>
          <span v-else class="ls-spinner"></span>
        </button>
        <p class="ls-alt-text">
          {{ $t('signup.alreadyHaveAccount') }}
          <a href="#" class="ls-alt-link" @click.prevent="$router.push('/')">{{ $t('signup.signInLink') }}</a>
        </p>
      </div>

      <!-- Step 2: OTP verification -->
      <div v-if="signupStep === 2">
        <p class="ls-step-hint">
          {{ $t('signup.otpSentHintPrefix') }} <strong>{{ signupForm.phone_number }}</strong>.
        </p>
        <div class="ls-field">
          <v-text-field
            v-model="signupForm.otp_code"
            :label="$t('signup.verificationCodeLabel')"
            :error-messages="otpError ? [otpError] : []"
            autofocus
          />
        </div>
        <div class="ls-row-btns">
          <button class="ls-btn ls-btn--ghost" @click="signupStep = 1">
            {{ $t('signup.backButton') }}
          </button>
          <button
            class="ls-btn ls-btn--primary ls-btn--flex"
            :class="{ 'ls-btn--loading': signupLoading }"
            :disabled="signupLoading"
            @click="verifyOtp()"
          >
            <span v-if="!signupLoading">{{ $t('signup.verifyButton') }}</span>
            <span v-else class="ls-spinner"></span>
          </button>
        </div>
      </div>

      <!-- Step 3: Profile details -->
      <div v-if="signupStep === 3">
        <p class="ls-step-hint">{{ $t('signup.step3Hint') }}</p>

        <div class="ls-field">
          <v-text-field
            v-model="profileForm.full_name"
            :label="$t('user.fullName') + ' *'"
            :error-messages="profileErrors.full_name ? [profileErrors.full_name] : []"
            :hint="$t('signup.fullNameHint')"
          />
        </div>
        <div class="ls-field">
          <v-text-field
            v-model="profileForm.dob"
            :label="$t('signup.dateOfBirthLabel') + ' *'"
            type="date"
            :error-messages="profileErrors.dob ? [profileErrors.dob] : []"
          />
        </div>
        <div class="ls-field">
          <v-text-field
            v-model="profileForm.password"
            :label="$t('login.password') + ' *'"
            :type="showRegPass ? 'text' : 'password'"
            :append-icon="showRegPass ? 'visibility' : 'visibility_off'"
            :append-icon-cb="() => (showRegPass = !showRegPass)"
            :error-messages="profileErrors.password ? [profileErrors.password] : []"
            :hint="$t('signup.passwordMinCharsHint')"
          />
        </div>
        <div class="ls-field">
          <v-text-field
            v-model="profileForm.confirm_password"
            :label="$t('user.confirmPassword') + ' *'"
            :type="showRegPassConfirm ? 'text' : 'password'"
            :append-icon="showRegPassConfirm ? 'visibility' : 'visibility_off'"
            :append-icon-cb="() => (showRegPassConfirm = !showRegPassConfirm)"
            :error-messages="profileErrors.confirm_password ? [profileErrors.confirm_password] : []"
          />
        </div>
        <div class="ls-field">
          <v-text-field
            v-model="profileForm.email"
            :label="$t('signup.emailOptionalLabel')"
            type="email"
            :error-messages="profileErrors.email ? [profileErrors.email] : []"
          />
        </div>
        <div class="ls-field">
          <v-select
            v-model="profileForm.gender"
            :items="genderOptions"
            item-text="label"
            item-value="value"
            :label="$t('signup.genderOptionalLabel')"
            clearable
          />
        </div>

        <!-- Address section -->
        <div class="ls-section-divider">
          <span class="ls-section-title">{{ $t('signup.deliveryAddressOptional') }} <span class="ls-optional">{{ $t('signup.optionalLabel') }}</span></span>
          <span class="ls-section-hint">{{ $t('signup.addressSectionHint') }}</span>
        </div>

        <div class="ls-field">
          <v-select
            v-model="addressForm.type"
            :items="addressTypes"
            item-text="text"
            item-value="value"
            :label="$t('signup.addressTypeLabel')"
            clearable
            :error-messages="profileErrors.address_type ? [profileErrors.address_type] : []"
          />
        </div>

        <template v-if="addressForm.type === 'House'">
          <div class="ls-field-row">
            <div class="ls-field ls-field--half">
              <v-text-field v-model="addressForm.house_number" :label="$t('signup.houseNumberLabel')" />
            </div>
            <div class="ls-field ls-field--half">
              <v-text-field v-model="addressForm.floor" :label="$t('signup.floorOptionalLabel')" />
            </div>
          </div>
          <div class="ls-field">
            <v-text-field v-model="addressForm.street" :label="$t('signup.streetLabel')" />
          </div>
        </template>

        <template v-else-if="addressForm.type === 'Apartment'">
          <div class="ls-field">
            <v-text-field v-model="addressForm.building_name" :label="$t('signup.buildingNameLabel')" />
          </div>
          <div class="ls-field-row">
            <div class="ls-field ls-field--half">
              <v-text-field v-model="addressForm.house_number" :label="$t('signup.apartmentNumberLabel')" />
            </div>
            <div class="ls-field ls-field--half">
              <v-text-field v-model="addressForm.floor" :label="$t('signup.floorLabel')" />
            </div>
          </div>
          <div class="ls-field">
            <v-text-field v-model="addressForm.street" :label="$t('signup.streetLabel')" />
          </div>
        </template>

        <template v-else-if="addressForm.type === 'Office'">
          <div class="ls-field">
            <v-text-field v-model="addressForm.building_name" :label="$t('signup.buildingNameLabel')" />
          </div>
          <div class="ls-field-row">
            <div class="ls-field ls-field--half">
              <v-text-field v-model="addressForm.company" :label="$t('signup.companyLabel')" />
            </div>
            <div class="ls-field ls-field--half">
              <v-text-field v-model="addressForm.floor" :label="$t('signup.floorLabel')" />
            </div>
          </div>
          <div class="ls-field">
            <v-text-field v-model="addressForm.street" :label="$t('signup.streetLabel')" />
          </div>
        </template>

        <template v-if="addressForm.type">
          <div class="ls-field-row">
            <div class="ls-field ls-field--half">
              <v-select
                v-model="addressForm.governorate_id_fk"
                :items="governorates"
                item-text="governorate_name"
                item-value="governorate_id"
                :label="$t('columns.governorate')"
                clearable
                @change="onGovernorateChange"
              />
            </div>
            <div class="ls-field ls-field--half">
              <v-select
                v-model="addressForm.city_id_fk"
                :items="cities"
                item-text="city_name"
                item-value="city_id"
                :label="$t('columns.city')"
                clearable
                :disabled="!addressForm.governorate_id_fk"
              />
            </div>
          </div>
          <div class="ls-field">
            <CountryPhoneInput
              v-model="addressForm.phone_number"
              :label="$t('signup.addressPhoneNumberOptional')"
              @update:isValid="addressPhoneValid = $event"
            />
          </div>
          <div class="ls-field">
            <v-text-field
              v-model="addressForm.additional_directions"
              :label="$t('signup.additionalDirectionsOptional')"
            />
          </div>
          <div class="ls-field">
            <v-text-field
              v-model="addressForm.name"
              :label="$t('signup.addressLabelHint')"
              :error-messages="profileErrors.address_name ? [profileErrors.address_name] : []"
            />
          </div>
        </template>

        <div class="ls-row-btns" style="margin-top: 8px;">
          <button
            class="ls-btn ls-btn--ghost"
            @click="signupStep = 1; signupForm.otp_code = null"
          >
            {{ $t('signup.backButton') }}
          </button>
          <button
            class="ls-btn ls-btn--success ls-btn--flex"
            :class="{ 'ls-btn--loading': signupLoading }"
            :disabled="signupLoading"
            @click="createProfile()"
          >
            <span v-if="!signupLoading">{{ $t('signup.createAccountButton') }}</span>
            <span v-else class="ls-spinner"></span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import swal from 'sweetalert'
import CountryPhoneInput from './shared/CountryPhoneInput.vue'

export default {
  components: { CountryPhoneInput },

  data() {
    return {
      // Signup
      signupStep: 1,
      signupLoading: false,
      signupPhoneValid: false,
      step1Error: '',
      otpError: '',
      signupForm: {
        phone_number: '',
        otp_code: ''
      },

      // Step 3 profile
      showRegPass: false,
      showRegPassConfirm: false,
      profileErrors: {},
      profileForm: {
        full_name: '',
        dob: '',
        password: '',
        confirm_password: '',
        email: '',
        gender: null
      },

      // Step 3 address
      addressForm: {
        governorate_id_fk: null,
        city_id_fk: null,
        type: null,
        name: '',
        house_number: '',
        floor: '',
        building_name: '',
        company: '',
        street: '',
        phone_number: '',
        additional_directions: ''
      },
      addressPhoneValid: false,
      governorates: [],
      cities: [],

      genderOptions: [
        { label: this.$t('signup.maleOption'), value: 'male' },
        { label: this.$t('signup.femaleOption'), value: 'female' }
      ],
      addressTypes: [
        { text: this.$t('signup.houseType'), value: 'House' },
        { text: this.$t('signup.apartmentType'), value: 'Apartment' },
        { text: this.$t('signup.officeType'), value: 'Office' }
      ]
    }
  },

  mounted() {
    axios.get('api/mobile/governorate/list').then(res => {
      this.governorates = res.data.data || res.data
    }).catch(() => {})
  },

  methods: {
    onGovernorateChange(governorateId) {
      this.addressForm.city_id_fk = null
      this.cities = []
      if (!governorateId) return
      axios.get(`api/mobile/city/list/${governorateId}`).then(res => {
        this.cities = res.data.data || res.data
      }).catch(() => {})
    },

    hasAddressData() {
      const a = this.addressForm
      return !!(a.type)
    },

    // ─── Step 1: Send OTP ────────────────────────────────────────────────────
    sendOtp() {
      this.step1Error = ''
      if (!this.signupPhoneValid) {
        this.step1Error = this.$t('signup.validPhoneRequiredError')
        return
      }
      this.signupLoading = true
      axios.post('api/verify-phone-number', { phone_number: this.signupForm.phone_number })
        .then(() => {
          this.signupStep = 2
        })
        .catch(error => {
          this.step1Error = error.response ? error.response.data.message : this.$t('signup.failedToSendCodeError')
        })
        .finally(() => {
          this.signupLoading = false
        })
    },

    // ─── Step 2: Verify OTP ──────────────────────────────────────────────────
    verifyOtp() {
      this.otpError = ''
      if (!this.signupForm.otp_code) {
        this.otpError = this.$t('signup.verificationCodeRequiredError')
        return
      }
      this.signupLoading = true
      axios.post('api/verify-otp-code', this.signupForm)
        .then(() => {
          this.signupStep = 3
        })
        .catch(error => {
          this.otpError = error.response ? error.response.data.message : this.$t('signup.verificationFailedError')
        })
        .finally(() => {
          this.signupLoading = false
        })
    },

    // ─── Step 3: Create Profile ──────────────────────────────────────────────
    createProfile() {
      this.profileErrors = {}
      const p = this.profileForm
      const a = this.addressForm

      if (!p.full_name.trim()) {
        this.$set(this.profileErrors, 'full_name', this.$t('signup.fullNameRequiredError'))
      }
      if (!p.dob) {
        this.$set(this.profileErrors, 'dob', this.$t('signup.dobRequiredError'))
      }
      if (!p.password || p.password.length < 6) {
        this.$set(this.profileErrors, 'password', this.$t('signup.passwordMinLengthError'))
      }
      if (!p.confirm_password) {
        this.$set(this.profileErrors, 'confirm_password', this.$t('signup.confirmPasswordRequiredError'))
      } else if (p.password !== p.confirm_password) {
        this.$set(this.profileErrors, 'confirm_password', this.$t('signup.passwordsDoNotMatchError'))
      }
      if (p.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email)) {
        this.$set(this.profileErrors, 'email', this.$t('signup.validEmailRequiredError'))
      }
      // if the user started filling address, require both type and label
      const addressStarted = !!(a.type || a.house_number || a.street || a.building_name || a.governorate_id_fk)
      if (addressStarted) {
        if (!a.type) this.$set(this.profileErrors, 'address_type', this.$t('signup.addressTypeRequiredError'))
        if (!a.name || !a.name.trim()) this.$set(this.profileErrors, 'address_name', this.$t('signup.addressLabelRequiredError'))
      }

      if (Object.keys(this.profileErrors).length > 0) return

      this.signupLoading = true

      const profilePayload = {
        phone_number: this.signupForm.phone_number,
        full_name: p.full_name.trim(),
        dob: p.dob,
        password: p.password,
        fcm_token: 'web'
      }
      if (p.email) profilePayload.email = p.email
      if (p.gender) profilePayload.gender = p.gender

      axios.post('api/mobile/create-profile', profilePayload)
        .then(res => {
          const token = res.data.token
          if (this.hasAddressData()) {
            return this.createAddress(token).then(() => token)
          }
          return Promise.resolve(token)
        })
        .then(token => {
          swal(this.$t('signup.welcomeTitle'), this.$t('signup.accountCreatedSuccessfully'), 'success')
            .then(() => {
              this.$emit('logged-in', token)
            })
        })
        .catch(error => {
          swal(this.$t('signup.registrationFailedTitle'), error.response ? error.response.data.message : this.$t('invoice.somethingWentWrong'), 'error')
        })
        .finally(() => {
          this.signupLoading = false
        })
    },

    // ─── Create address then set as active ───────────────────────────────────
    createAddress(token) {
      const a = this.addressForm
      const authHeader = { Authorization: `Bearer ${token}` }

      const addressPayload = {
        type: a.type,
        name: a.name ? a.name.trim() : a.type
      }
      if (a.governorate_id_fk) addressPayload.governorate_id_fk = a.governorate_id_fk
      if (a.city_id_fk) addressPayload.city_id_fk = a.city_id_fk
      if (a.house_number) addressPayload.house_number = a.house_number
      if (a.floor) addressPayload.floor = a.floor
      if (a.building_name) addressPayload.building_name = a.building_name
      if (a.company) addressPayload.company = a.company
      if (a.street) addressPayload.street = a.street
      if (a.phone_number && this.addressPhoneValid) addressPayload.phone_number = a.phone_number
      if (a.additional_directions) addressPayload.additional_directions = a.additional_directions

      return axios.post('api/mobile/customer-addresses', addressPayload, { headers: authHeader })
        .then(res => {
          const addressId = res.data.data
            ? res.data.data.customer_address_id
            : res.data.customer_address_id
          return axios.post(`api/mobile/set-delivery-address/${addressId}`, {}, { headers: authHeader })
        })
    }
  }
}
</script>

<style scoped>
.ls-reg-page {
  min-height: 100vh;
  background-color: #FFC83D;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 60px 16px 40px;
  font-family: 'Segoe UI', Arial, sans-serif;
}

/* Logo */
.ls-logo-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 28px;
}

.ls-brand-name {
  font-size: 32px;
  font-weight: 800;
  font-style: italic;
  color: #771AFF;
  margin: -35px 0 0;
  letter-spacing: 0.5px;
}

/* Card */
.ls-card {
  background: #ffffff;
  border-radius: 24px;
  padding: 36px 32px 28px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
}

.ls-card-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 16px;
}

/* Step indicator */
.ls-steps {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}

.ls-step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 700;
  background: #e0e0e0;
  color: #888;
  transition: background 0.2s, color 0.2s;
}

.ls-step--active {
  background: #771AFF;
  color: #fff;
}

/* Step hint */
.ls-step-hint {
  font-size: 13px;
  color: #666;
  margin: 0 0 16px;
}

/* Fields */
.ls-field {
  margin-bottom: 4px;
}

.ls-field--half {
  flex: 1;
}

.ls-field-row {
  display: flex;
  gap: 12px;
}

.ls-field >>> .v-input__slot::after {
  border-color: #771AFF !important;
}

.ls-field >>> .v-label--active {
  color: #771AFF !important;
}

/* Error text */
.ls-error-text {
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
}

/* Section divider */
.ls-section-divider {
  border-top: 1px solid #ebebeb;
  padding-top: 14px;
  margin: 16px 0 8px;
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
}

.ls-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #333;
}

.ls-optional {
  font-weight: 400;
  color: #888;
}

.ls-section-hint {
  font-size: 12px;
  color: #aaa;
}

/* Buttons */
.ls-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 13px 20px;
  font-size: 15px;
  font-weight: 700;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;
  letter-spacing: 0.4px;
  margin-top: 12px;
}

.ls-btn--primary {
  width: 100%;
  background-color: #771AFF;
  color: #fff;
}

.ls-btn--primary:hover:not(:disabled) {
  background-color: #6010e0;
}

.ls-btn--success {
  background-color: #1DB95A;
  color: #fff;
}

.ls-btn--success:hover:not(:disabled) {
  background-color: #17a04d;
}

.ls-btn--ghost {
  background-color: transparent;
  color: #771AFF;
  border: 1.5px solid #771AFF;
  padding: 12px 18px;
}

.ls-btn--ghost:hover {
  background-color: #f3ecff;
}

.ls-btn--flex {
  flex: 1;
}

.ls-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.ls-btn--loading {
  pointer-events: none;
}

/* Row of buttons (back + action) */
.ls-row-btns {
  display: flex;
  gap: 10px;
  align-items: center;
}

/* Spinner */
.ls-spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 3px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ls-spin 0.7s linear infinite;
}

@keyframes ls-spin {
  to { transform: rotate(360deg); }
}

/* Sign-in link */
.ls-alt-text {
  margin-top: 18px;
  text-align: center;
  font-size: 14px;
  color: #555;
}

.ls-alt-link {
  color: #771AFF;
  font-weight: 700;
  text-decoration: none;
}

.ls-alt-link:hover {
  text-decoration: underline;
}
</style>



// WEBPACK FOOTER //
// src/components/customerRegistration.vue